// src/lib/permissions.js — RBAC no front (espelho da matriz de papéis · migração 11).
// O front ESCONDE; quem garante é a RLS (auth_user_role() no Postgres).
// Papéis: tenant_admin · gestor_comercial_financeiro · supervisor_obras ·
//         funcionario_rh · funcionario_campo · cliente_final

import { tenant } from '../context/tenantContext.js';

// Mapa papel → escopos. '*' = tudo. 'modulo:*' = todas as ações do módulo.
export const ROLE_PERMS = {
  tenant_admin: ['*'],
  gestor_comercial_financeiro: [
    'comercial:*', 'financeiro:*', 'inteligencia:ver', 'suporte:abrir',
  ],
  supervisor_obras: [
    'operacoes:*', 'equipe:ponto_proprio', 'equipe:ponto_equipe',
    'financeiro:reembolso_solicitar', 'suporte:abrir',
  ],
  funcionario_rh: ['equipe:*', 'suporte:abrir'],
  funcionario_campo: ['equipe:ponto_proprio', 'financeiro:reembolso_solicitar', 'suporte:abrir'],
  cliente_final: ['inteligencia:portal_cliente', 'suporte:abrir'],
};

// Rótulos de exibição dos papéis (UI de Usuários).
export const ROLE_LABELS = {
  tenant_admin: 'Administrador Geral',
  gestor_comercial_financeiro: 'Gestor Comercial/Financeiro',
  supervisor_obras: 'Supervisor de Obras',
  funcionario_rh: 'Analista de RH',
  funcionario_campo: 'Funcionário de Campo',
  cliente_final: 'Cliente (portal)',
};

// Papel → tipo_usuario legado compatível (controla o shell e a RLS legada).
export const TIPO_LEGADO_FROM_ROLE = {
  tenant_admin: 'administrador',
  gestor_comercial_financeiro: 'administrador',
  funcionario_rh: 'administrador',
  supervisor_obras: 'supervisor',
  funcionario_campo: 'funcionario',
  cliente_final: 'cliente',
};

// Fallback mapeado do legado (users_profile.tipo_usuario) — mesmo critério do auth_user_role().
export function roleFromTipoLegado(tipo) {
  return ({
    administrador: 'tenant_admin',
    supervisor: 'supervisor_obras',
    funcionario: 'funcionario_campo',
    cliente: 'cliente_final',
    super_admin: 'tenant_admin',
  })[tipo] || null;
}

/** Papéis efetivos do usuário logado (matriz multi-papel · feature 002). */
export function rolesAtuais() {
  const rs = tenant.roles;
  if (rs && rs.length) return rs;
  const fb = tenant.role || roleFromTipoLegado(tenant.profile?.tipo_usuario);
  return fb ? [fb] : [];
}

/** UNIÃO dos escopos de todos os papéis do usuário. */
function permsUnion() {
  const out = new Set();
  for (const r of rolesAtuais()) for (const p of (ROLE_PERMS[r] || [])) out.add(p);
  return out;
}

/** Checa escopo na união dos papéis. Ex.: hasPermission('financeiro:aprovar'). */
export function hasPermission(scope) {
  if (!scope) return false;
  const perms = permsUnion();
  if (!perms.size) return false;
  if (perms.has('*') || perms.has(scope)) return true;
  const mod = scope.split(':')[0];
  return perms.has(mod + ':*');
}

/** Módulo visível se ALGUM papel tem escopo nele. Ex.: hasModule('financeiro'). */
export function hasModule(modulo) {
  if (!modulo) return false;
  const perms = permsUnion();
  if (perms.has('*')) return true;
  for (const p of perms) if (p === modulo || p.startsWith(modulo + ':')) return true;
  return false;
}

// ── Gating da sidebar desktop ───────────────────────────────────────
// page → escopo mínimo p/ exibir. Página desconhecida = admin-only (default restritivo).
export const PAGE_SCOPE = {
  // Inteligência
  dashboard: 'inteligencia:ver', bi: 'inteligencia:ver', mapa: 'inteligencia:ver',
  relatorios: 'inteligencia:ver', assistente: 'inteligencia:ver',
  // Operações
  obras: 'operacoes:ver', tarefas: 'operacoes:ver', cronograma: 'operacoes:ver',
  obras_entregues: 'operacoes:ver', garantias: 'operacoes:ver', etapas: 'operacoes:ver',
  // Comercial
  clientes: 'comercial:ver', fornecedores: 'comercial:ver', terceirizados: 'comercial:ver',
  contratos: 'comercial:ver', orcamentos: 'comercial:ver', portfolio: 'comercial:ver',
  // Financeiro
  financeiro: 'financeiro:ver', contas_receber: 'financeiro:ver', inadimplentes: 'financeiro:ver',
  contas_pagar: 'financeiro:ver', despesas: 'financeiro:ver', notas: 'financeiro:ver',
  compras: 'financeiro:ver', dre: 'financeiro:ver', recorrencias: 'financeiro:ver',
  nfse_tomadas: 'financeiro:ver',
  reembolsos: 'financeiro:ver',
  // Equipe/RH
  rh: 'equipe:ver', funcionarios: 'equipe:ver', docs_funcionarios: 'equipe:ver',
  ausencias: 'equipe:ver', ferias_rh: 'equipe:ver', ponto: 'equipe:ver',
  horas: 'equipe:ver', espelho_ponto: 'equipe:ver',
  // Suporte / Sistema
  meus_chamados: 'suporte:abrir',
  atividades: 'sistema:admin', configuracoes: 'sistema:admin', usuarios: 'sistema:admin',
  arquivos: 'sistema:admin', upgrade: 'sistema:admin', sobre: 'sistema:admin',
};

// ── Gating por PLANO (Fase 1) ───────────────────────────────────────────────
// Universo de páginas "gateáveis" (itens de módulo da sidebar admin). Páginas fora
// deste set (detalhes, drawers, perfil) nunca são bloqueadas pelo plano.
const GATEABLE_PAGES = new Set([
  'dashboard','bi','mapa','assistente','obras','clientes','fornecedores','terceirizados','contratos',
  'orcamentos','portfolio','tarefas','cronograma','obras_entregues','garantias','etapas',
  'financeiro','contas_receber','inadimplentes','contas_pagar','despesas','reembolsos',
  'notas','compras','nfse_tomadas','dre','recorrencias','rh','funcionarios','docs_funcionarios','ausencias',
  'ferias_rh','ponto','horas','espelho_ponto','relatorios','atividades','meus_chamados',
  'configuracoes','usuarios','arquivos','upgrade','sobre',
]);
// Sempre liberadas (perfil/conta/suporte/assinatura) — o trial precisa poder assinar e sair.
const PLAN_NEUTRAL = new Set(['dashboard','configuracoes','upgrade','meus_chamados','sobre']);

// Módulos por plano. Trial = lista homologada (Rafael 04/06). Planos pagos: '*' (full)
// por ora — TODO confirmar subsets de canteiro (Operações Básicas) e obra (+Comercial/Financeiro).
// Subsets homologados (matriz Rafael 04/06 · docs/PLANOS-PRECIFICACAO.md):
// canteiro = operações básicas de campo (RDO/tarefas/ponto GPS) + despesas, SEM financeiro
// complexo, SEM comercial, SEM IA · obra = canteiro + Comercial + Financeiro completo manual,
// SEM reembolsos (automação PRO) e SEM IA · empresa/enterprise = tudo (IA via PRO_PLANS).
const CANTEIRO_MODULES = [
  'dashboard','mapa','rh','assistente',  // assistente = vitrine/upsell em todos os planos (003)
  'obras','clientes','etapas','tarefas','cronograma','obras_entregues','garantias',
  'despesas',
  'funcionarios','docs_funcionarios','ausencias','ferias_rh','ponto','horas','espelho_ponto',
  'arquivos','relatorios','atividades',
  'usuarios','configuracoes','upgrade','meus_chamados','sobre',
];
const OBRA_MODULES = [
  ...CANTEIRO_MODULES,
  'bi','financeiro','contas_receber','contas_pagar','dre','inadimplentes',
  'notas','compras','recorrencias','orcamentos',
  'contratos','fornecedores','terceirizados','portfolio',
];

export const PLAN_MODULES = {
  trial: new Set([
    'dashboard','bi','mapa','assistente', // Inteligência (a IA segue gated por PRO_PLANS; assistente = vitrine)
    'obras','clientes','etapas','tarefas','cronograma','contratos',
    'despesas','dre',
    'rh','espelho_ponto','ausencias',
    'usuarios',  // FASE 3b: admin do trial vê a equipe demo e as credenciais p/ testar o campo
    'configuracoes','upgrade','meus_chamados','sobre',
  ]),
  canteiro: new Set(CANTEIRO_MODULES),
  obra: new Set(OBRA_MODULES),
  empresa: '*',
  enterprise: '*',
};

/** Página liberada pelo PLANO do tenant? (detalhes/neutras sempre liberadas) */
export function pageAllowedByPlan(page, plano) {
  if (!page) return true;
  if (PLAN_NEUTRAL.has(page)) return true;
  const allow = PLAN_MODULES[plano];
  if (!allow || allow === '*') return true;     // plano desconhecido/full → não bloqueia
  if (!GATEABLE_PAGES.has(page)) return true;    // rota de detalhe/drawer → libera
  return allow.has(page);
}

/** Filtra os grupos da sidebar pelo papel logado E pelo plano (remove itens e grupos vazios). */
export function filterNavGroups(groups) {
  const plano = tenant.config?.plano;
  return groups
    .map((g) => ({ ...g, items: (g.items || []).filter((it) =>
      hasPermission(PAGE_SCOPE[it.page] || 'sistema:admin') && pageAllowedByPlan(it.page, plano)) }))
    .filter((g) => g.items.length > 0);
}
